import DropdownCommon, {
  DropdownItemStyled,
} from 'components/common/DropdownCommon';
import ArrowDownIcon from 'components/common/Icons/ArrowDownIcon';
import {
  CALCULATOR_FORM_COUNTRY_NAME,
  CALCULATOR_FORM_RECEIVER_GET_NAME,
  CALCULATOR_FORM_SEND_TO_NAME,
  CURRENCY_FLAG_REFERENCES,
} from 'constants';
import useCalculatorRoundingPublic from 'features/calculatorRounding/hooks/useCalculatorRoundingPublic';
import useCalculatorRoundingStore from 'features/calculatorRounding/hooks/useCalculatorRoundingStore';
import { Fade } from 'helpers/animation';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

export const CALCULATOR_FORM_CURRENCY_NAME = 'calculator-form-currency-name';

const CustomCurrencyDropdown = ({
  maxSendLimit,
  currenciesSupport,
  currentRateValue,
  information,
  setInformation,
  validation,
  setValidation,
  currentCurrencySelected,
  handleSendAmountOnChange,
  handleGetReceiverAmountValidation,
}) => {
  const [isShowDropdown, setShowDropdown] = useState(false);

  const isOnlyReceiverCurrency = currenciesSupport?.length === 1;

  const { fetchCurrentFee } = useCalculatorRoundingPublic();
  const { isCalculatorRounding, isRateFetching } = useCalculatorRoundingStore();

  const handleFetchCurrentFee = async (information, setInformation) => {
    await fetchCurrentFee(
      information,
      setInformation,
      information[CALCULATOR_FORM_COUNTRY_NAME]?.code,
      maxSendLimit,
      false,
    );
  };

  useEffect(() => {
    const isCountryChange = information?.isCountryChange;
    const sendAmount =
      Number(information[CALCULATOR_FORM_SEND_TO_NAME] || 0) || '';
    const receiverAmount =
      Number(information[CALCULATOR_FORM_RECEIVER_GET_NAME] || 0) || '';

    if (receiverAmount) {
      const [sendValue, receiverValue] = handleSendAmountOnChange(
        sendAmount,
        true,
      );

      let newInformation = {
        ...information,
        [CALCULATOR_FORM_SEND_TO_NAME]: sendValue,
        [CALCULATOR_FORM_RECEIVER_GET_NAME]: receiverValue,
      };

      let newValidation = {
        ...validation,
        [CALCULATOR_FORM_RECEIVER_GET_NAME]: '',
      };

      newValidation = handleGetReceiverAmountValidation(
        newInformation,
        newValidation,
      );
      setValidation(newValidation);

      newInformation = {
        ...newInformation,
        currentRate: currentRateValue,
      };

      if (isCountryChange) return;
      handleFetchCurrentFee(newInformation, setInformation);
    }
  }, [currentRateValue]);

  const handleCurrencySelected = async (currency) => {
    const newInformation = {
      ...information,
      currentCurrencySelected: currency,
    };
    setInformation(newInformation);

    setShowDropdown(false);
  };

  const handleDropdownOnToggle = () => {
    if (isOnlyReceiverCurrency) {
      setShowDropdown(false);
      return;
    }

    setShowDropdown(!isShowDropdown);
  };

  const renderDropdownToggleChildren = () => (
    <>
      <CountryName $isOnlyReceiverCurrency={isOnlyReceiverCurrency}>
        {currentCurrencySelected && (
          <>
            <FlagIcon
              src={
                CURRENCY_FLAG_REFERENCES[currentCurrencySelected?.currencyCode]
              }
            />
            {currentCurrencySelected?.currencyCode}
          </>
        )}
      </CountryName>
      {!isOnlyReceiverCurrency && (
        <ArrowDownIcon
          color="#fff"
          styles={{
            position: 'absolute',
            right: '8px',
            top: '22px',
            transform: isShowDropdown && 'rotate(180deg)',
          }}
        />
      )}
    </>
  );
  const renderDropdownMenuChildren = () => (
    <>
      {currenciesSupport?.map((currency, index) => (
        <DropdownItemStyled
          key={index}
          eventKey={index}
          onClick={() => handleCurrencySelected(currency)}
          style={{
            borderRadius: '8px',
            height: '50px',
          }}
        >
          <CountryNameItem
            style={{
              color: 'var(--c-primary)',
            }}
          >
            <FlagIcon src={CURRENCY_FLAG_REFERENCES[currency.currencyCode]} />
            {currency.currencyCode}
          </CountryNameItem>
        </DropdownItemStyled>
      ))}
    </>
  );

  const getStyleDropdown = () => {
    const common = { border: 'none' };
    return common;
  };
  const getStyleDropdownToggle = () => {
    const common = {
      textTransform: 'initial',
      paddingRight: '40px',
      fontWeight: 600,
      minHeight: '50px',
    };
    return common;
  };
  const getStyleDropdownMenu = () => {
    const common = {
      borderRadius: '8px',
      background: '#fff',
      minWidth: '123px',
      transform: 'translate(-63px, 51px)',
    };
    return common;
  };

  return (
    <CurrencyDropdownStyled>
      <DropdownCommon
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        renderDropdownToggleChildren={renderDropdownToggleChildren}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
        styleDropdown={getStyleDropdown()}
        styleDropdownToggle={getStyleDropdownToggle()}
        styleDropdownMenu={getStyleDropdownMenu()}
      />
    </CurrencyDropdownStyled>
  );
};

const CurrencyDropdownStyled = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 27px;
  right: 10.5px;
  ${Fade};
`;

const CountryName = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: #fff;

  width: 123px;
  height: 50px;
  background-color: var(--c-primary);
  justify-content: flex-end;
  align-items: center;
  padding-right: ${(props) =>
    props.$isOnlyReceiverCurrency ? '16px' : '46px'};
  display: flex;
  position: absolute;
  top: 1px;
  right: 0.5px;
  margin-right: -10px;
  border-radius: 0 11px 11px 0px;
`;
const CountryNameItem = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--c-primary);

  width: 123px;
  height: 100%;
  display: flex;
  align-items: center;
`;
const FlagIcon = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
  object-fit: cover;
  border: 1px transparent;
  border-radius: 50%;
  margin-right: 4px;
`;

export default CustomCurrencyDropdown;
